<template>
  <div class="page-wrapper">
    <NavbarApp />
    <div class="person-detail-page">
      <div v-if="person" class="detail-card">
        <div class="person-header-section">
          <div class="person-image-wrapper">
            <img
              :src="getImagePath(person.image)"
              :alt="person.name"
              class="person-detail-img"
            >
          </div>
          <div class="person-info-content">
            <h1 class="person-name">{{ person.name }}</h1>
            <p class="person-category">{{ person.category.charAt(0).toUpperCase() + person.category.slice(1) }}</p>
            <p class="person-email">Email: <a :href="`mailto:${person.email}`">{{ person.email }}</a></p>
          </div>
        </div>

        <div class="person-bio-section">
          <h2 class="section-title">{{ $t('personDetail.biographyTitle') || 'Biography' }}</h2>
          <div class="person-description" :class="{ 'single-paragraph-centered': translatedParagraphs.length === 1 }">
            <p v-for="(paragraph, index) in translatedParagraphs" :key="index" class="bio-paragraph">
              {{ paragraph }}
            </p>
          </div>
        </div>

        <!-- Articles section 
        <div v-if="personArticles && personArticles.length > 0" class="person-articles-section">
          <h2 class="section-title"> {{ $t('team.articlePublished') }} </h2>
          <div class="articles-grid">
            <router-link
              v-for="article in personArticles"
              :key="article.id"
              :to="{ name: 'ArticleDetail', params: { id: article.id }}"
              class="article-card"
            >
              <div class="article-image">
                <img :src="article.image" :alt="article.title">
              </div>
              <div class="article-content">
                <h3 class="article-title">{{ article.title }}</h3>
                <p class="article-date">{{ formatDate(article.date) }}</p>
                <p class="article-excerpt">{{ article.excerpt }}</p>
              </div>
            </router-link>
          </div>
        </div>-->

        <div class="back-link-section">
          <router-link to="/team" class="back-button">
            &larr; {{ $t('common.backToTeam') || 'Back to Team' }}
          </router-link>
        </div>
      </div>

      <div v-else class="not-found-section">
        <h1 class="not-found-title">{{ $t('common.personNotFound') || 'Person Not Found' }}</h1>
        <p class="not-found-message">
          {{ $t('personDetail.notFoundMessage') || 'The team member you are looking for does not exist.' }}
        </p>
        <router-link to="/team" class="back-button">
          &larr; {{ $t('common.backToTeam') || 'Back to Team' }}
        </router-link>
      </div>
    </div>
    <Section4 />
  </div>
</template>

<script>
import { teamAssociated } from '@/data/associated'
import NavbarApp from '../HomePage/navbar-law.vue'
import Section4 from '../HomePage/section-4.vue'
import articlesData from '@/database/news.json'

export default {
  name: 'PersonDetail',
  components: {
    NavbarApp,
    Section4
  },
  props: ['id'],
  data() {
    return {
      person: null
    }
  },
  created() {
    this.fetchPersonDetails()
  },
  methods: {
    fetchPersonDetails() {
      this.person = teamAssociated.find(member => member.id === this.id)
    },
    getImagePath(imageName) {
      try {
        return require(`@/assets/contact/team/${imageName}`)
      } catch (e) {
        console.warn(`[PersonDetail] Image not found: ${imageName}. Using fallback.`, e)
        return require('@/assets/logo.png')
      }
    },
    formatDate(date) {
      if (!date) return ''
      const enDate = new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
      const roDate = new Date(date).toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
      return `${enDate} / ${roDate}`
    }
  },
  computed: {
    translatedParagraphs() {
      if (!this.person) return []
      return this.$i18n.locale === 'ro' ? (this.person.paragraphs_ro || []) : (this.person.paragraphs || [])
    },
    personArticles() {
      if (!this.person) return []
      return articlesData
        .filter(article => article.authorID === this.person.id)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
    }
  },
  watch: {
    id: 'fetchPersonDetails'
  }
}
</script>
<style scoped>

.person-articles-section {
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid #eee;
}

.articles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 30px;
}

.article-card {
  text-decoration: none;
  color: inherit;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
}

.article-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

.article-image {
  width: 100%;
  height: 200px;
  overflow: hidden;
}

.article-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.article-card:hover .article-image img {
  transform: scale(1.05);
}

.article-content {
  padding: 20px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.article-title {
  font-size: 1.2rem;
  color: #2c3e50;
  margin: 0 0 10px;
  line-height: 1.4;
}

.article-date {
  color: #777;
  font-size: 0.9rem;
  margin-bottom: 10px;
}

.article-excerpt {
  color: #666;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@media (max-width: 768px) {
  .articles-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .article-image {
    height: 180px;
  }

  .article-content {
    padding: 15px;
  }

  .article-title {
    font-size: 1.1rem;
  }
}
/*
  All the styles specific to the person detail page
  (e.g., .person-detail-page, .detail-card, .person-header-section,
  .person-image-wrapper, .person-detail-img, .person-info-content,
  .person-name, .person-category, .person-email, .person-bio-section,
  .section-title, .person-description, .bio-paragraph, .back-link-section,
  .back-button, .not-found-section, .not-found-title,
  .not-found-message, and their respective media queries)
  should be placed here.
*/

/*
  General Page and Container Styles
*/
.person-detail-page {
  padding: 80px 20px; /* Increased top padding to account for a hypothetical fixed navbar */
  max-width: 1000px; /* Wider content area */
  margin: 0 auto;
  
  color: #333;
}

.detail-card {
  background-color: #ffffff;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1); /* Softer, deeper shadow */
  overflow: hidden; /* Ensures child elements respect border-radius */
  padding: 40px;
}

/*
  Header Section (Image and Basic Info)
*/
.person-header-section {
  display: flex;
  flex-direction: row; /* Default: image left, info right */
  align-items: center;
  gap: 40px; /* Space between image and text */
  margin-bottom: 50px;
  padding-bottom: 30px;
  border-bottom: 1px solid #eee; /* Subtle separator */
}

.person-image-wrapper {
  flex-shrink: 0;
  width: 280px; /* Larger image size */
  height: 280px;
  border-radius: 50%; /* Circular image */
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15); /* Shadow for the image */
  border: 5px solid #bbd55a; /* Accent border */
  display: flex;
  justify-content: center;
  align-items: center;
}

.person-detail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top; /* Focus on the face/upper body */
  display: block;
}

.person-info-content {
  flex-grow: 1;
  text-align: left;
}

.person-name {
  font-size: 3.5rem; /* Larger, more impactful name */
  color: #2c3e50;
  margin: 0 0 10px;
  font-weight: 700;
  line-height: 1.1;
}

.person-category {
  font-size: 1.3rem;
  color: #777;
  margin-bottom: 15px;
  font-style: italic;
  font-weight: 400;
}

.person-email {
  font-size: 1.1rem;
  color: #555;
}

.person-email a {
  color: #bbd55a;
  text-decoration: none;
  transition: color 0.3s ease;
}

.person-email a:hover {
  color: #bbd55a;
  text-decoration: underline;
}

/*
  Biography Section
*/
.person-bio-section {
  margin-bottom: 50px;
}

.section-title {
  font-size: 2.2rem;
  color: #2c3e50;
  margin-bottom: 30px;
  text-align: center;
  position: relative;
  padding-bottom: 10px;
}

.section-title::after {
  content: '';
  display: block;
  width: 80px;
  height: 4px;
  background-color: #bbd55a;
  margin: 10px auto 0;
  border-radius: 2px;
}

.person-description {
  column-count: 2; /* Two-column layout for paragraphs */
  column-gap: 40px; /* Space between columns */
  text-align: justify; /* Justify text for a clean look */
}

/* New CSS for single paragraph centering */
.person-description.single-paragraph-centered {
  column-count: 1; /* Override to single column */
  text-align: center; /* Center the text */
  max-width: 700px; /* Optional: limit width for better readability when centered */
  margin: 0 auto; /* Center the block itself */
}

.bio-paragraph {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #444;
  margin: 0px; /* Space between paragraphs */
  /* Remove margin-top: 0; from here to avoid potential issues with multiple paragraphs */
  break-inside: avoid-column; /* Prevents paragraphs from splitting across columns */
  text-align: justify;
  text-justify: inter-word; 
}

/*
  Back Button Section
*/
.back-link-section {
  text-align: center;
  margin-top: 40px;
}

.back-button {
  display: inline-flex; /* Use flex for icon and text alignment */
  align-items: center;
  gap: 8px; /* Space between arrow and text */
  padding: 15px 30px;
  background-color: #bbd55a;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: background-color 0.3s ease, transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.back-button:hover {
  background-color: #bbd55a;
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
}

.back-button:active {
  background-color: #bbd55a;
  transform: translateY(0);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

/*
  Not Found Section Styles
*/
.not-found-section {
  text-align: center;
  padding: 80px 20px;
  background-color: #ffffff;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
}

.not-found-title {
  font-size: 3rem;
  color: #e74c3c; /* A strong red for error */
  margin-bottom: 20px;
}

.not-found-message {
  font-size: 1.3rem;
  color: #777;
  margin-bottom: 40px;
}

/*
  Responsive Adjustments
*/
@media (max-width: 992px) {
  .person-detail-page {
    padding: 60px 15px;
  }
  .detail-card {
    padding: 30px;
  }
  .person-header-section {
    flex-direction: column; /* Stack image and info vertically */
    text-align: center;
    gap: 30px;
  }
  .person-info-content {
    text-align: center; /* Center text in column layout */
  }
  .person-name {
    font-size: 2.8rem;
  }
  .section-title {
    font-size: 2rem;
  }
  .person-description {
    column-count: 1; /* Single column for biography on smaller screens */
  }
  /* Ensure the single-paragraph-centered still works on smaller screens */
  .person-description.single-paragraph-centered {
    text-align: center; /* Override back to center if column-count was 1 anyway */
    max-width: none; /* Remove max-width on smaller screens if it causes issues */
  }
}

@media (max-width: 768px) {
  .person-detail-page {
    padding: 40px 10px;
  }
  .person-image-wrapper {
    width: 200px;
    height: 200px;
  }
  .person-name {
    font-size: 2.2rem;
  }
  .person-category,
  .person-email {
    font-size: 1rem;
  }
  .section-title {
    font-size: 1.8rem;
  }
  .bio-paragraph {
    font-size: 1rem;
  }
  .back-button {
    padding: 12px 25px;
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .detail-card {
    padding: 20px;
  }
  .person-image-wrapper {
    width: 150px;
    height: 150px;
  }
  .person-name {
    font-size: 1.8rem;
  }
  .person-category,
  .person-email {
    font-size: 0.9rem;
  }
  .section-title {
    font-size: 1.5rem;
  }
  .bio-paragraph {
    font-size: 0.9rem;
  }
  .not-found-title {
    font-size: 2rem;
  }
  .not-found-message {
    font-size: 1rem;
  }
}
</style>